import { Immutable } from "immer";
import React from "react";

import type { MediaSection as Value } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { originalUrl } from "../../utils/wagtail";

import bannerStyles from "../../styles/composable/banner.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/media_sections/media_section.html`` (#33285).
 *
 * - The legacy class attr emitted `` banner--reverse `` (extra whitespace)
 *   only when ``orientation == 'right'``; whitespace differences in class
 *   attrs are fine.
 * - ``description`` is the serialized RichTextBlock string, which INCLUDES
 *   the ``<div class="rich-text">`` wrapper -- exactly what the legacy
 *   ``{{ self.description|richtext }}`` emitted inside
 *   ``<div class="banner__copy alt-copy">``; injecting it is exact parity.
 */
export const MediaSection = (props: IProps): React.JSX.Element => {
    return (
        <section
            className={concatClassNames([
                bannerStyles.banner,
                "ptb",
                props.value.orientation === "right"
                    ? bannerStyles.bannerReverse
                    : null,
            ])}
        >
            <div className={bannerStyles.bannerFigure}>
                {/* Decorative images have empty alt attr */}
                <img
                    className="img-responsive"
                    src={originalUrl(props.value.image)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
            </div>

            <div
                className={concatClassNames([
                    bannerStyles.bannerContent,
                    bannerStyles.bannerContentRtq,
                ])}
            >
                <h5 className={bannerStyles.bannerTitle}>
                    {props.value.title}
                </h5>
                <div
                    className={concatClassNames([
                        bannerStyles.bannerCopy,
                        "alt-copy",
                    ])}
                    dangerouslySetInnerHTML={{
                        __html: props.value.description ?? "",
                    }}
                />
            </div>
        </section>
    );
};
